import React from 'react';
import EditClientBillingInfoFields, {
    FormValue,
    formValueToCreateClientRecordInvitationRequest,
    getDefaultFormValue,
} from '../../../../../../shared/EditClientBillingInfo/EditClientBillingInfoFields';
import useOrganizationClientRecordBillingDetails from '../../../../../../../hooks/api/useOrganizationClientRecordBillingDetails';
import { AnimateLoadedContent } from '../../../../../../v2BaseComponents/Loader';
import SideDrawerFormContainer, {
    ConfirmDirtyStateFormCloseContainer,
    SideDrawerDirtyFormExitValidationTracker,
} from '../../../../../../v2BaseComponents/Form/SideDrawerFormContainer';
import { Form } from '../../../../../../v2BaseComponents/Form';
import { getIntl } from '../../../../../../../utils/intl';

const intl = getIntl('profiles.editClientBillingInfoSideDrawerContent', {
    update: 'Update',
});

export type EditClientRecordBillingInformationSideDrawerContentOwnProps = {
    clientRecordId: string;
    onClose: (force: boolean) => void;
};

export type EditClientRecordBillingInformationSideDrawerContentProps =
    EditClientRecordBillingInformationSideDrawerContentOwnProps;

const emptyBillingDetails: FormValue = {
    name: '',
    email: '',
    phoneNumber: '',
};

const EditClientRecordBillingInformationSideDrawerContent: React.FC<
    EditClientRecordBillingInformationSideDrawerContentProps
> = ({ clientRecordId, onClose }) => {
    const { isLoadingBillingDetails, isUpdatingBillingDetails, updateBillingDetails, billingDetails } =
        useOrganizationClientRecordBillingDetails({
            organizationClientRecordId: clientRecordId,
            onUpdatedBillingDetails: () => onClose(true),
        });

    return (
        <AnimateLoadedContent
            isLoading={isLoadingBillingDetails || isUpdatingBillingDetails || !billingDetails}
            key={JSON.stringify(billingDetails)}
        >
            {() => (
                <ConfirmDirtyStateFormCloseContainer>
                    <Form
                        fillContainer
                        initialValues={billingDetails ? getDefaultFormValue(billingDetails) : emptyBillingDetails}
                        onSubmit={(values) => {
                            const request = formValueToCreateClientRecordInvitationRequest(values);
                            updateBillingDetails(request);
                        }}
                        isDisabled={isLoadingBillingDetails || isUpdatingBillingDetails}
                        forceStackFields
                    >
                        {({ isSubmitting, handleSubmit }) => {
                            return (
                                <SideDrawerFormContainer
                                    isDisabled={isLoadingBillingDetails || isUpdatingBillingDetails || isSubmitting}
                                    onSubmit={handleSubmit}
                                    onCancel={() => onClose(false)}
                                    saveText={intl('update')}
                                    saveIcon="CheckCircle"
                                >
                                    <SideDrawerDirtyFormExitValidationTracker />
                                    <EditClientBillingInfoFields />
                                </SideDrawerFormContainer>
                            );
                        }}
                    </Form>
                </ConfirmDirtyStateFormCloseContainer>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(EditClientRecordBillingInformationSideDrawerContent);
